import { type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import { AlertTriangle, ArrowUpRight, Lock, Timer } from 'lucide-react';
import {
  COPYRIGHT_STATUS_LABELS,
  type ArticleSummary,
  type CopyrightStatus,
  type SegmentStatus,
} from '@en3/shared';
import { Badge, Select, Textarea } from '@/components/ui/card';
import { cn } from '@/lib/cn';

/**
 * 文章卡片：目录页（教材树 / 全部文章）与首页「继续学习」共用同一个渲染器。
 *
 * 三个关键取舍：
 *
 * 1. **整卡是一个 `<button>`，不是 `<a>`，也不在卡里再套链接。**
 *    卡片里将来会长出"编辑 / 删除"等次要操作，嵌套交互元素（按钮套链接）在键盘与
 *    读屏下都会出现"焦点落到哪里、回车触发谁"的歧义。所以用 `onOpen` 回调把
 *    "去哪一页"的决定留给页面，卡片只负责"被点击"。
 *
 * 2. **难度档位由分数算出，而不是读后端字段。**
 *    `difficultyScore` 是 0–100 的连续值，而用户需要的是"这篇对我难不难"的三档判断。
 *    阈值写在这里（<40 易 / 40–65 中 / >65 难）而不是散落在各页面，
 *    否则同一篇文章在目录页和阅读页会显示不同的难度档。
 *
 * 3. **诚实展示"还没分句"与"仅公开目录"两种空状态。**
 *    正文没导入的文章、以及平台只收录了目录元数据的教材，如果什么都不显示，
 *    用户会以为是加载失败。这两种状态各自有一条明确说明。
 */

export interface ArticleCardProps {
  article: ArticleSummary;
  /** 点击整卡时打开该文章 */
  onOpen: (article: ArticleSummary) => void;
  /** 首页「继续学习」用：显示一条进度条 */
  progressPct?: number | undefined;
  /** 首页「继续学习」用：上次读到第几句 */
  lastGlobalIndex?: number | undefined;
  /** 紧凑模式：不显示来源与版权行（首页小卡片用） */
  compact?: boolean;
  className?: string;
}

/** 难度分 → 档位。40 / 65 是"易-中-难"的分界，全站唯一来源。 */
function difficultyBand(score: number): 'easy' | 'medium' | 'hard' {
  if (score < 40) return 'easy';
  if (score <= 65) return 'medium';
  return 'hard';
}

const DIFFICULTY_LABELS = { easy: '易', medium: '中', hard: '难' } as const;
const DIFFICULTY_TONES = { easy: 'success', medium: 'warning', hard: 'danger' } as const;

/** 分句状态：pending 是导入后的正常瞬时状态，必须说清"还在处理"而不是"内容有问题"。 */
const SEGMENT_LABELS: Record<SegmentStatus, string> = {
  pending: '待分句',
  processing: '正在分句',
  done: '已分句',
};

function copyrightLabel(status: CopyrightStatus): string {
  return COPYRIGHT_STATUS_LABELS[status] ?? status;
}

export function ArticleCard({
  article,
  onOpen,
  progressPct,
  lastGlobalIndex,
  compact = false,
  className,
}: ArticleCardProps) {
  // 主标题取中文（`titleZh`）优先：站点是中文界面，用户在列表里找的是"那篇讲什么"，
  // 英文原题放在副标题里仍然可见，两者都不丢。
  const primaryTitle = article.titleZh ?? article.title;
  const secondaryTitle = article.titleZh ? article.title : null;
  const band = article.difficultyScore === null ? null : difficultyBand(article.difficultyScore);

  return (
    <button
      type="button"
      onClick={() => onOpen(article)}
      className={cn(
        'group flex w-full flex-col gap-2 rounded-lg border bg-card p-3 text-left text-card-foreground',
        'transition-colors hover:border-primary hover:bg-accent/40',
        className,
      )}
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <h4 className="text-sm font-semibold break-words">{primaryTitle}</h4>
          {secondaryTitle && (
            <p className="mt-0.5 text-xs break-words text-muted-foreground">{secondaryTitle}</p>
          )}
        </div>
        <ArrowUpRight
          aria-hidden
          className="mt-0.5 size-4 shrink-0 text-muted-foreground group-hover:text-primary"
        />
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        <Badge tone="muted">
          {article.wordCount} 词 / {article.sentenceCount} 句
        </Badge>

        {band && (
          <Badge tone={DIFFICULTY_TONES[band]} title={`难度分 ${Math.round(article.difficultyScore ?? 0)}（0–100）`}>
            难度 {DIFFICULTY_LABELS[band]}
            {article.difficultyScore !== null && ` ${Math.round(article.difficultyScore)}`}
          </Badge>
        )}

        {article.cefrEstimate && <Badge tone="primary">{article.cefrEstimate}</Badge>}

        {article.longSentenceCount > 0 ? (
          // 长难句是"这篇值得精读"的信号，因此单独高亮成 destructive 色（而不是普通灰徽章）：
          // 目录里扫一眼就能挑出该下功夫的文章。
          <Badge
            tone="danger"
            title="点开文章后可以逐句解析主干、从句与翻译"
            className="font-medium"
          >
            <AlertTriangle aria-hidden className="size-3" />
            {article.longSentenceCount} 个长难句
          </Badge>
        ) : (
          <Badge tone="muted">无长难句</Badge>
        )}

        <Badge tone={article.segmentStatus === 'done' ? 'muted' : 'warning'}>
          {article.segmentStatus === 'done' ? null : <Timer aria-hidden className="size-3" />}
          {SEGMENT_LABELS[article.segmentStatus]}
        </Badge>

        {article.visibility === 'private' && (
          <Badge tone="muted" title="只有你自己能看到这篇">
            <Lock aria-hidden className="size-3" />
            私有
          </Badge>
        )}
      </div>

      {typeof progressPct === 'number' && (
        <div>
          <div
            className="h-1.5 w-full overflow-hidden rounded-full bg-muted"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(progressPct)}
            aria-label="阅读进度"
          >
            <div className="h-full rounded-full bg-primary" style={{ width: `${Math.min(100, Math.max(0, progressPct))}%` }} />
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            已读 {Math.round(progressPct)}% · 上次读到第 {(lastGlobalIndex ?? 0) + 1} 句
          </p>
        </div>
      )}

      {!compact && (
        <p className="text-xs text-muted-foreground">
          来源：{article.source ?? '未标注'}
          {article.author ? ` · ${article.author}` : ''} · {copyrightLabel(article.copyrightStatus)}
        </p>
      )}
    </button>
  );
}

/**
 * 只有目录元数据、还没有正文的教材里，用这一行占位。
 *
 * 单独导出而不是塞进卡片：它描述的是"这个单元还没有正文"这一整体状态，
 * 与某篇文章无关，放在文章列表的空档位上。
 */
export function ArticleEmptyHint({
  text = '这个单元还没有导入正文。正文须由具备使用授权的教师或用户导入。',
  className,
}: {
  text?: string;
  className?: string;
}) {
  return (
    <p className={cn('rounded-md border border-dashed px-3 py-3 text-xs text-muted-foreground', className)}>
      {text}
    </p>
  );
}

/**
 * 表单控件包装：`FormSelect` / `FormTextarea`。
 *
 * 为什么需要它们：`components/ui/card.tsx` 里的 `Select` / `Textarea` 是用
 * `HTMLAttributes<…>` 声明的（而不是 `SelectHTMLAttributes` / `TextareaHTMLAttributes`），
 * 因此**不接受 `value` / `onChange` / `rows` / `placeholder` 这些属性**。
 * 这个项目里不允许改 UI 组件文件，所以在**一处**做类型断言，让其余代码保持正常写法 ——
 * 断言集中在这里还有一个好处：将来 UI 组件修正了类型，只需要删掉这两个包装。
 *
 * 注意这里断言的只是"属性类型"，元素渲染与事件绑定仍然是 React 原生的。
 */
/** UI 组件的属性类型（`HTMLAttributes<…> & { children }`），只用来做断言，不参与渲染。 */
type UiProps = Record<string, unknown> & { children: React.ReactNode };

export function FormSelect(props: SelectHTMLAttributes<HTMLSelectElement>) {
  return <Select {...(props as UiProps)} />;
}

export function FormTextarea(props: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <Textarea {...(props as UiProps)} />;
}
